if (!customElements.get('complementary-product')) {
    class ComplementaryProducts extends HTMLElement {
        constructor() {
            super();
            this.productId = this.getAttribute('recommendation-product-id');
            this.recommendationType = this.getAttribute('recommendation-type');
            this.recommendationSectionName = this.getAttribute(
                'recommendation-section'
            );
            // Block setting: replace quick view (eye) with direct add to cart (+)
            this.directAddEnabled = this.getAttribute('data-direct-add') === 'true';
            // Check if productRecommendations are loaded and use it in condition to prevent re-render of product
            this.didProductRecommendationLoad = false;
            this.elObj = {
                recommendationEl: '[products-data]',
                recommendationTitle: '[title]',
            };
            // Listen for event when component is in view
            this.addEventListener('observe:elementInView', () => {
                if (this.didProductRecommendationLoad) return;
                this.fetchData();
            });
        }

        queryHTMLData(htmlString, elementToQuery) {
            const html = new DOMParser().parseFromString(htmlString, 'text/html');
            return html.querySelector(elementToQuery).innerHTML;
        }

        renderTitle() {
            const getSectionTitle =
                this.querySelector('template').content.firstElementChild.cloneNode(
                    true
                );

            this.prepend(getSectionTitle);
        }

        showProductLayout() {
            this.classList.remove('recommendation-inactive');
        }

        generateProductLayouts(textData) {
            const returnedHTMLAfterQuery = this.queryHTMLData(
                textData,
                this.elObj.recommendationEl
            );
            if (returnedHTMLAfterQuery && returnedHTMLAfterQuery.trim()) {
                try {
                    const elToAppendData = this.querySelector(
                        this.elObj.recommendationEl
                    );
                    elToAppendData.innerHTML = returnedHTMLAfterQuery;
                    this.renderTitle();
                    if (this.directAddEnabled) this.enableDirectAdd();
                    this.showProductLayout();
                } catch (error) {
                    console.log(error);
                }
            }
        }

        /* ---------- Direct add to cart (+) ---------- */

        enableDirectAdd() {
            const plusIcon =
                '<svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M7 1v12M1 7h12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
            const checkIcon =
                '<svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M2 7.5l3 3 7-7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

            this.querySelectorAll('.recommendation__item').forEach((item) => {
                // Theme renders no button for sold-out items: leave those alone
                const originalForm = item.querySelector('.product-card-item__form');
                if (!originalForm || item.querySelector('.cp-direct-add')) return;

                const originalBtn = originalForm.querySelector('button');
                const variantInput =
                    originalForm.querySelector('[name="id"]') ||
                    item.querySelector('[name="id"]');
                const productLink = item.querySelector('a[href*="/products/"]');

                const form = document.createElement('form');
                form.className = 'product-card-item__form cp-direct-add';
                form.method = 'post';
                form.action = `${window.Shopify.routes.root}cart/add`;
                form.noValidate = true;
                if (variantInput && variantInput.value) {
                    form.dataset.variantId = variantInput.value;
                }
                if (productLink) {
                    form.dataset.productUrl = productLink
                        .getAttribute('href')
                        .split('?')[0]
                        .split('#')[0];
                }

                // Keep only the theme's btn* classes for the look, drop quick-view hooks
                const themeBtnClasses = originalBtn
                    ? [...originalBtn.classList].filter((c) => /^btn/.test(c)).join(' ')
                    : '';

                const btn = document.createElement('button');
                btn.type = 'submit';
                btn.className = `${themeBtnClasses} cp-direct-add__btn`.trim();
                btn.setAttribute('aria-label', 'Add to cart');
                btn.innerHTML = `
                    <span class="btn--text">
                        <span class="cp-direct-add__icon cp-direct-add__icon--plus">${plusIcon}</span>
                        <span class="cp-direct-add__icon cp-direct-add__icon--check">${checkIcon}</span>
                    </span>
                    <span class="cp-direct-add__spinner" aria-hidden="true"></span>`;
                form.appendChild(btn);

                // Stop theme-level delegated click handlers (quick view) from firing
                btn.addEventListener('click', (e) => e.stopPropagation());
                form.addEventListener('submit', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.handleDirectAdd(form, btn);
                });

                originalForm.style.display = 'none';
                originalForm.after(form);
            });
        }

        async resolveVariantId(productUrl) {
            if (!productUrl) return null;
            const res = await fetch(`${productUrl}.js`, {
                headers: { Accept: 'application/json' },
            });
            if (!res.ok) return null;
            const productData = await res.json();
            const firstAvailable = productData.variants.find((v) => v.available);
            return firstAvailable ? firstAvailable.id : null;
        }

        async handleDirectAdd(form, btn) {
            if (btn.disabled) return;
            btn.disabled = true;
            btn.classList.remove('cp-direct-add__btn--added');
            btn.classList.add('cp-direct-add__btn--loading');

            try {
                const variantId =
                    form.dataset.variantId ||
                    (await this.resolveVariantId(form.dataset.productUrl));
                if (!variantId) throw new Error('No available variant found');
                form.dataset.variantId = variantId;

                const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                    body: JSON.stringify({
                        items: [{ id: Number(variantId), quantity: 1 }],
                    }),
                });
                const data = await res.json();
                if (!res.ok) {
                    throw new Error(data.description || data.message || 'Add to cart failed');
                }

                btn.classList.add('cp-direct-add__btn--added');
                btn.setAttribute('aria-label', 'Added to cart');
                setTimeout(() => {
                    btn.classList.remove('cp-direct-add__btn--added');
                    btn.setAttribute('aria-label', 'Add to cart');
                }, 1500);

                // Hook for cart drawer / cart count refresh
                document.dispatchEvent(
                    new CustomEvent('cp:cart-added', {
                        bubbles: true,
                        detail: { items: data.items, variantId: Number(variantId) },
                    })
                );
            } catch (error) {
                console.error('[complementary-product] direct add failed:', error);
            } finally {
                btn.disabled = false;
                btn.classList.remove('cp-direct-add__btn--loading');
            }
        }

        /* -------------------------------------------- */

        fetchData() {
            this.didProductRecommendationLoad = true;
            HELPER_UTIL.fetchData({
                method: 'GET',
                responseType: 'text',
                url: `${window.Shopify.routes.root}recommendations/products?product_id=${this.productId}&section_id=${this.recommendationSectionName}&intent=${this.recommendationType}`,
                onsuccess: (textData) => {
                    this.generateProductLayouts(textData);
                    // this.setProductsContainer().active();
                },
                onerror: (error) => {
                    console.log(error);
                },
            });
        }

        connectedCallback() {
            console.log('Complementary products connected');
        }
    }

    customElements.get('complementary-product') ||
        customElements.define('complementary-product', ComplementaryProducts);
}